import { memo, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import MUIDataTable from "mui-datatables";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SaveIcon from "@mui/icons-material/Save";
import { FormInputText } from "../../components/form-components/FormInputText";
import { useFieldArray, useForm } from "react-hook-form";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { FormAutoComplete } from "../../components/form-components/FormAutoComplete";
import toast from "react-hot-toast";
import axios from "axios";
import { FormInputDropdown } from "../../components/form-components/FormInputDropdown";

// ✅ Helper to normalize weight_ids — handles array, object {}, or null
const normalizeWeightIds = (weight_ids) => {
  if (!weight_ids) return [];
  if (Array.isArray(weight_ids)) return weight_ids;
  if (typeof weight_ids === "object") return Object.values(weight_ids);
  return [];
};

function BulkModal({
  data,
  open,
  handleClose,
  selectedRow: selectedProductRow,
  loadInitialData,
}) {
  const [loader, setLoader] = useState(false);
  const [weightList, setWeightList] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);

  const methods = useForm();
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    setValue,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "bulk_provision",
  });

  const selectedProductWatch = watch("product_id");

  // ✅ When product dropdown changes, update weight list
  useEffect(() => {
    if (!selectedProductWatch || !data?.length) return;

    const selectedProduct = data.find(
      (item) => item.id === selectedProductWatch?.value,
    );

    if (selectedProduct) {
      // ✅ normalize weight_ids — handles array, object {}, or string
      let weights = [];

      if (Array.isArray(selectedProduct.weight_ids)) {
        weights = selectedProduct.weight_ids;
      } else if (
        typeof selectedProduct.weight_ids === "object" &&
        selectedProduct.weight_ids !== null
      ) {
        // ✅ object like { "0": { id: 1, itemName: "0.5" } }
        weights = Object.values(selectedProduct.weight_ids);
      } else if (typeof selectedProduct.weight_ids === "string") {
        // Comma separated string - match with variation
        const weightIdArray = selectedProduct.weight_ids
          .split(",")
          .map(Number);
        weights =
          selectedProduct.variation
            ?.filter((v) => weightIdArray.includes(v.weight_id))
            ?.map((v) => ({
              id: v.weight_id,
              label: v.weight_id.toString(),
              name: v.weight_id.toString(),
              value: v.weight_id,
            })) || [];
      }

      const formattedWeights = weights.map((w) => ({
        id: w.id ?? w.weight_id,
        label: w.itemName ?? w.label ?? w.id?.toString(),
        name: w.itemName ?? w.name ?? w.id?.toString(),
        value: w.id ?? w.weight_id,
      }));

      setWeightList(formattedWeights);
    } else {
      setWeightList([]);
    }
  }, [selectedProductWatch, data]);

  // ✅ When weightList updates, match and set weight_id values in form fields
  useEffect(() => {
    if (!weightList.length || !fields.length) return;

    fields.forEach((field, index) => {
      const currentWeightId =
        typeof field.weight_id === "object"
          ? field.weight_id?.id
          : field.weight_id;

      const matchedWeight = weightList.find((w) => w.id === currentWeightId);
      if (matchedWeight) {
        setValue(`bulk_provision.${index}.weight_id`, matchedWeight);
      }
    });
  }, [weightList, fields.length]);

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        display: false,
        setCellProps: () => ({ style: { width: "30px" } }),
        customBodyRenderLite: (dataIndex) => (
          <FormInputText
            size="small"
            key={fields[dataIndex]?.id}
            name={`bulk_provision[${dataIndex}].id`}
            control={control}
            label="ID"
            disabled
            mandatory="true"
            disable={true}
            defaultValue={fields[dataIndex].variation_id}
          />
        ),
      },
    },
    {
      name: "weight_id",
      label: "Weight",
      options: {
        setCellProps: () => ({ style: { width: "100px" } }),
        customBodyRenderLite: (dataIndex) => (
          <FormAutoComplete
            size="small"
            name={`bulk_provision[${dataIndex}].weight_id`}
            control={control}
            label="Weight"
            multiple={false}
            dataArray={weightList || []}
            errors={errors}
            mandatory="true"
          />
        ),
      },
    },
    {
      name: "min_quantity",
      label: "Min Qty",
      options: {
        setCellProps: () => ({ style: { width: "100px" } }),
        customBodyRenderLite: (dataIndex) => (
          <FormInputText
            size="small"
            key={fields[dataIndex]?.id}
            name={`bulk_provision[${dataIndex}].min_quantity`}
            control={control}
            label="Min Quantity"
            mandatory="true"
            defaultValue={fields[dataIndex].min_quantity}
          />
        ),
      },
    },
    {
      name: "max_quantity",
      label: "Max Qty",
      options: {
        setCellProps: () => ({ style: { width: "100px" } }),
        customBodyRenderLite: (dataIndex) => (
          <FormInputText
            size="small"
            key={fields[dataIndex]?.id}
            name={`bulk_provision[${dataIndex}].max_quantity`}
            control={control}
            label="Max Quantity"
            mandatory="true"
            defaultValue={fields[dataIndex].max_quantity}
          />
        ),
      },
    },
    {
      name: "egg_price",
      label: "Egg Price",
      options: {
        setCellProps: () => ({ style: { width: "100px" } }),
        customBodyRenderLite: (dataIndex) => (
          <FormInputText
            size="small"
            key={fields[dataIndex]?.id}
            name={`bulk_provision[${dataIndex}].egg_price`}
            control={control}
            label="Price"
            mandatory="true"
            defaultValue={fields?.[dataIndex]?.egg_price}
          />
        ),
      },
    },
    {
      name: "eggless_price",
      label: "Eggless Price",
      options: {
        setCellProps: () => ({ style: { width: "100px" } }),
        customBodyRenderLite: (dataIndex) => (
          <FormInputText
            size="small"
            key={fields[dataIndex]?.id}
            name={`bulk_provision[${dataIndex}].eggless_price`}
            control={control}
            label="Price"
            mandatory="true"
            defaultValue={fields?.[dataIndex]?.eggless_price}
          />
        ),
      },
    },
    {
      name: "Delete",
      options: {
        setCellProps: () => ({ style: { width: "100px" } }),
        customBodyRenderLite: (dataIndex) => (
          <div className="actionBtns">
            <Button
              key={fields[dataIndex]?.id}
              aria-label="delete"
              onClick={() => {
                setPendingDeleteIndex(dataIndex);
                setConfirmOpen(true);
              }}
            >
              <DeleteOutlineOutlinedIcon color="error" fontSize="medium" />
            </Button>
          </div>
        ),
      },
    },
  ];

  const addVariationHandler = () => {
    append({
      weight_id: "",
      min_quantity: 1,
      max_quantity: 1,
      egg_price: null,
      eggless_price: null,
    });
  };

  const removeHandler = async (index) => {
    const removedItem = fields[index];
    const dbId = removedItem?.bulk_provision_id;

    try {
      if (dbId) {
        await axios.get(`/bulk-pricing/${dbId}`);
        toast.success("Deleted successfully");
        remove(index);
        await loadInitialData();
      } else {
        remove(index);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete item");
    }
  };

  const handleCloseModal = () => {
    setDeletedIds([]);
    remove();
    handleClose();
  };

  const onSubmit = async (data) => {
    const isValid = await methods.trigger();
    if (!isValid) {
      toast.error("Please fill all required fields.");
      return;
    }

    const seen = new Set();
    for (let i = 0; i < data.bulk_provision.length; i++) {
      const row = data.bulk_provision[i];
      const weightId =
        typeof row.weight_id === "object" ? row.weight_id?.id : row.weight_id;

      const minQty = Number(row.min_quantity);
      const maxQty = Number(row.max_quantity);
      const price = Number(row.egg_price);

      if (!weightId || isNaN(minQty) || isNaN(maxQty) || isNaN(price)) {
        toast.error(`Row ${i + 1} has missing or invalid fields.`);
        return;
      }

      const key = `${weightId}-${minQty}-${maxQty}-${price}`;
      if (seen.has(key)) {
        toast.error(
          `Duplicate found: Row ${
            i + 1
          } has same Weight, Min Qty, and Max Qty as a previous row.`,
        );
        return;
      }
      seen.add(key);
    }

    const payload = {
      product_id: data?.product_id?.value,
      bulk_provision: data.bulk_provision.map((row) => ({
        id: row.bulk_provision_id,
        weight_id:
          typeof row.weight_id === "object" ? row.weight_id?.id : row.weight_id,
        min_quantity: Number(row.min_quantity),
        max_quantity: Number(row.max_quantity),
        egg_price: Number(row.egg_price),
        eggless_price:
          row.eggless_price !== null ? Number(row.eggless_price) : null,
      })),
      deleted_ids: deletedIds,
    };

    try {
      setLoader(true);
      let response;
      if (selectedProductRow?.id > 0) {
        response = await axios.put(
          `/bulk-provision/${selectedProductRow?.id}`,
          payload,
        );
      } else {
        response = await axios.post("/bulk-provision", payload);
      }

      if (!response?.data?.error) {
        toast.success(response?.data?.message);
        loadInitialData();
      } else {
        let errDesc = "";
        if (response?.data?.data) {
          Object.values(response?.data?.data).forEach((error) => {
            errDesc += error + "\n";
          });
        } else {
          errDesc = response?.data?.message;
        }
        toast.error(errDesc);
      }
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setLoader(false);
      handleCloseModal();
    }
  };

  const confirmDelete = () => {
    setConfirmOpen(false);
    removeHandler(pendingDeleteIndex);
    setPendingDeleteIndex(null);
  };

  const options = {
    selectableRows: false,
    filter: false,
    download: false,
    viewColumns: false,
    print: false,
    search: false,
    pagination: false,
    setTableProps: () => ({ size: "small" }),
  };

  // ✅ When modal opens, load existing bulk provision data
  useEffect(() => {
    if (open) {
      setDeletedIds([]);
      remove();
      methods.reset();

      if (selectedProductRow?.product_id) {
        const selectedProduct = data?.find(
          (item) => item.id === selectedProductRow.product_id,
        );

        if (selectedProduct) {
          // ✅ Use normalizeWeightIds to handle array OR object {}
          const formattedWeights = normalizeWeightIds(
            selectedProduct.weight_ids,
          ).map((w) => ({
            id: w.id,
            label: w.itemName,
            name: w.itemName,
            value: w.id,
          }));

          setWeightList(formattedWeights);

          setTimeout(() => {
            setValue("product_id", {
              value: selectedProduct.id,
              label: selectedProduct.product_name,
            });
          }, 0);

          const bulkPrices = selectedProductRow?.bulk_price || [];

          setTimeout(() => {
            bulkPrices.forEach((item) => {
              append({
                ...item,
                bulk_provision_id: item.id,
                weight_id: item.weight_id, // plain number/id — matched later by useEffect
              });
            });
          }, 50);
        }
      }
    }
  }, [open]);

  return (
    <>
      <Dialog open={open} onClose={handleCloseModal} maxWidth={"md"}>
        <DialogTitle className="text-primary">Bulk Provision</DialogTitle>
        <DialogContent>
          <Grid container columnSpacing={{ xs: 5 }}>
            <Grid item xs={6}>
              <FormAutoComplete
                name="product_id"
                control={control}
                label="Product"
                multiple={false}
                dataArray={data?.map((item) => ({
                  value: item?.id,
                  label: item?.product_name,
                }))}
                errors={errors}
                mandatory="true"
              />
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={addVariationHandler}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <MUIDataTable
                className="bg-light"
                data={fields || []}
                columns={columns}
                options={options}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained" color="error">
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            startIcon={<SaveIcon />}
            disabled={loader}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this Bulk Product?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="primary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(BulkModal);